import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { toast } from "sonner";

const PLAN_COLOR = {
  free: "border-border text-muted-foreground",
  pro: "border-emerald-500/40 text-emerald-400",
  pro_plus: "border-violet-500/40 text-violet-400",
};

const SOURCE_COLOR = {
  direct: "border-border text-muted-foreground",
  referral: "border-amber-500/40 text-amber-400",
  affiliate: "border-cyan-500/40 text-cyan-400",
};

const DURATIONS = [
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
  { label: "Forever", days: null, forever: true },
];

// Compact "2 days ago" / "5h ago" / "just now" formatter — picks the
// largest unit that's ≥ 1. Same style as the Mini App's history list
// so admins read the same vocabulary in both UIs.
const fmtRelative = (iso) => {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "now";
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
};

const fmtMoney = (n) => {
  if (n == null) return "—";
  if (n < 0.01) return "$0";
  return `$${n.toFixed(2)}`;
};

export default function Users() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [grantTarget, setGrantTarget] = useState(null);
  // iter39 — filter + sort state. Mirrored 1:1 with backend
  // `/admin/users` query params so the URL of the API call doubles as
  // a debuggable snapshot of what the admin is looking at.
  const [planFilter, setPlanFilter] = useState("all");      // "all"|"free"|"pro"
  const [activeFilter, setActiveFilter] = useState("all");  // "all"|"active"|"dormant"
  const [sourceFilter, setSourceFilter] = useState("all");  // "all"|"direct"|"referral"|"affiliate"
  const [sortBy, setSortBy] = useState("last_seen");        // see backend
  const [sortDir, setSortDir] = useState("desc");
  const [loading, setLoading] = useState(false);

  const buildParams = () => {
    const params = {};
    if (q) params.q = q;
    if (planFilter !== "all") params.plan = planFilter;
    if (activeFilter === "active") params.active = true;
    if (activeFilter === "dormant") params.active = false;
    if (sourceFilter !== "all") params.source = sourceFilter;
    params.sort_by = sortBy;
    params.sort_dir = sortDir;
    return params;
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users", { params: buildParams() });
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, planFilter, activeFilter, sourceFilter, sortBy, sortDir]);

  const onBlock = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/block`, { blocked: !u.is_blocked });
      toast.success(u.is_blocked ? "Unblocked" : "Blocked");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onRevoke = async (u) => {
    if (!confirm(`Revoke Pro for @${u.username || u.first_name}?`)) return;
    try {
      await api.post(`/admin/users/${u.id}/revoke`);
      toast.success("Subscription revoked");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col);
      setSortDir("desc");
    }
  };

  const onExportCsv = async () => {
    try {
      // Use axios with `responseType: blob` so the browser can save it
      // as a file — same auth cookie flows through `api`'s interceptor.
      const { data } = await api.get("/admin/users/export.csv", {
        params: buildParams(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `clipra-users-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length} users`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-5" data-testid="users-page">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <h1 className="font-display text-3xl tracking-tight">Users</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="search"
            placeholder="Search username or name…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="bg-secondary/30 border border-border px-3 py-1.5 text-sm w-56 font-mono focus:outline-none focus:border-foreground/40"
            data-testid="users-search"
          />
          <button
            onClick={onExportCsv}
            data-testid="users-export-csv"
            className="px-3 py-1.5 text-xs font-mono uppercase tracking-[0.16em] border border-border hover:bg-secondary/40 transition-colors"
          >
            Export CSV
          </button>
        </div>
      </div>

      {/* Filter bar — three compact selects on one row */}
      <div className="flex items-center gap-2 flex-wrap text-xs font-mono uppercase tracking-[0.14em]">
        <FilterSelect
          label="Plan"
          value={planFilter}
          onChange={setPlanFilter}
          options={[
            { v: "all", l: "All" },
            { v: "free", l: "Free" },
            { v: "pro", l: "Pro" },
          ]}
          testId="users-filter-plan"
        />
        <FilterSelect
          label="Activity"
          value={activeFilter}
          onChange={setActiveFilter}
          options={[
            { v: "all", l: "All" },
            { v: "active", l: "Active (≥ 1 job)" },
            { v: "dormant", l: "Dormant (0 jobs)" },
          ]}
          testId="users-filter-active"
        />
        <FilterSelect
          label="Source"
          value={sourceFilter}
          onChange={setSourceFilter}
          options={[
            { v: "all", l: "All" },
            { v: "direct", l: "Direct" },
            { v: "referral", l: "Referral" },
            { v: "affiliate", l: "Affiliate" },
          ]}
          testId="users-filter-source"
        />
        <div className="text-muted-foreground ml-auto" data-testid="users-count">
          {loading ? "Loading…" : `${rows.length} shown`}
        </div>
      </div>

      <div className="border border-border" data-testid="users-table">
        {/* ── Desktop (≥lg): grid-таблица как было ── */}
        <div className="hidden lg:block overflow-x-auto">
          <div className="min-w-[760px]">
        <div className="grid grid-cols-[1fr_80px_90px_70px_80px_110px_220px] gap-3 px-4 py-2.5 text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground border-b border-border bg-secondary/20">
          <div>User</div>
          <SortHeader col="last_seen" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort}>
            Plan
          </SortHeader>
          <div>Source</div>
          <SortHeader col="jobs" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort}>
            Jobs
          </SortHeader>
          <SortHeader col="ltv" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort}>
            LTV
          </SortHeader>
          <SortHeader col="last_job" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort}>
            Last job
          </SortHeader>
          <div className="text-right">Actions</div>
        </div>
        {rows.length === 0 && !loading && (
          <div className="px-4 py-12 text-center text-sm text-muted-foreground">
            No users match.
          </div>
        )}
        {rows.map((u) => (
          <div
            key={u.id}
            data-testid={`user-row-${u.id}`}
            className="grid grid-cols-[1fr_80px_90px_70px_80px_110px_220px] gap-3 px-4 py-3 text-sm border-b border-border last:border-b-0 items-center"
          >
            <div className="min-w-0">
              <Link
                to={`/admin/users/${u.id}`}
                data-testid={`user-link-${u.id}`}
                className="block truncate hover:underline"
              >
                {u.first_name || "—"} {u.last_name || ""}
                {u.is_blocked && (
                  <span className="ml-2 text-[10px] font-mono uppercase text-red-400 border border-red-500/40 px-1">
                    blocked
                  </span>
                )}
              </Link>
              <div className="text-xs text-muted-foreground font-mono truncate">
                @{u.username || "—"} · {u.telegram_user_id} · {(u.lang || "—").toUpperCase()} · seen {fmtRelative(u.last_seen_at)}
              </div>
            </div>
            <div>
              <span
                className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.14em] border ${PLAN_COLOR[u.plan] || PLAN_COLOR.free}`}
                data-testid={`user-plan-${u.id}`}
              >
                {u.plan}
              </span>
            </div>
            <div>
              <span
                className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.14em] border ${SOURCE_COLOR[u.source_label] || SOURCE_COLOR.direct}`}
                data-testid={`user-source-${u.id}`}
              >
                {u.source_label}
              </span>
            </div>
            <div
              className="font-mono text-sm tabular-nums"
              data-testid={`user-jobs-${u.id}`}
            >
              {u.jobs_total ?? 0}
            </div>
            <div
              className={`font-mono text-sm tabular-nums ${(u.ltv_usd ?? 0) > 0 ? "text-emerald-400" : "text-muted-foreground"}`}
              data-testid={`user-ltv-${u.id}`}
            >
              {fmtMoney(u.ltv_usd)}
            </div>
            <div
              className="font-mono text-xs text-muted-foreground"
              data-testid={`user-lastjob-${u.id}`}
            >
              {u.last_job_at ? fmtRelative(u.last_job_at) : "—"}
            </div>
            <div className="flex justify-end gap-1.5">
              <button
                onClick={() => setGrantTarget(u)}
                data-testid={`user-grant-${u.id}`}
                className="px-2.5 py-1 text-xs border border-border hover:bg-secondary/40 transition-colors"
              >
                Grant Pro
              </button>
              {u.plan !== "free" && (
                <button
                  onClick={() => onRevoke(u)}
                  className="px-2.5 py-1 text-xs border border-border text-muted-foreground hover:text-red-400 transition-colors"
                >
                  Revoke
                </button>
              )}
              <button
                onClick={() => onBlock(u)}
                data-testid={`user-block-${u.id}`}
                className={`px-2.5 py-1 text-xs border transition-colors ${
                  u.is_blocked
                    ? "border-emerald-500/40 text-emerald-400"
                    : "border-border hover:text-red-400"
                }`}
              >
                {u.is_blocked ? "Unblock" : "Block"}
              </button>
            </div>
          </div>
        ))}
          </div>
        </div>

        {/* ── Mobile (<lg): card layout ── */}
        <div className="lg:hidden divide-y divide-border">
          {rows.length === 0 && !loading && (
            <div className="px-4 py-12 text-center text-sm text-muted-foreground">
              No users match.
            </div>
          )}
          {rows.map((u) => (
            <UserCard
              key={u.id}
              u={u}
              onGrant={() => setGrantTarget(u)}
              onRevoke={() => onRevoke(u)}
              onBlock={() => onBlock(u)}
            />
          ))}
        </div>
      </div>

      <GrantDialog
        target={grantTarget}
        onClose={() => setGrantTarget(null)}
        onDone={() => {
          setGrantTarget(null);
          load();
        }}
      />
    </div>
  );
}

function UserCard({ u, onGrant, onRevoke, onBlock }) {
  return (
    <div
      data-testid={`user-row-${u.id}`}
      className="px-4 py-3 text-sm space-y-2"
    >
      {/* Row 1: Name + plan/source badges */}
      <div className="flex items-start justify-between gap-3 min-w-0">
        <div className="min-w-0 flex-1">
          <Link
            to={`/admin/users/${u.id}`}
            data-testid={`user-link-${u.id}`}
            className="block truncate hover:underline"
          >
            {u.first_name || "—"} {u.last_name || ""}
            {u.is_blocked && (
              <span className="ml-2 text-[10px] font-mono uppercase text-red-400 border border-red-500/40 px-1">
                blocked
              </span>
            )}
          </Link>
          <div className="text-xs text-muted-foreground font-mono truncate">
            @{u.username || "—"} · {u.telegram_user_id} · {(u.lang || "—").toUpperCase()}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span
            className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.14em] border ${PLAN_COLOR[u.plan] || PLAN_COLOR.free}`}
            data-testid={`user-plan-${u.id}`}
          >
            {u.plan}
          </span>
          <span
            className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.14em] border ${SOURCE_COLOR[u.source_label] || SOURCE_COLOR.direct}`}
            data-testid={`user-source-${u.id}`}
          >
            {u.source_label}
          </span>
        </div>
      </div>

      {/* Row 2: Stats grid */}
      <div className="grid grid-cols-3 gap-2 text-xs font-mono">
        <Stat label="Jobs" value={u.jobs_total ?? 0} testId={`user-jobs-${u.id}`} />
        <Stat
          label="LTV"
          value={fmtMoney(u.ltv_usd)}
          tone={(u.ltv_usd ?? 0) > 0 ? "ok" : "muted"}
          testId={`user-ltv-${u.id}`}
        />
        <Stat
          label="Last job"
          value={u.last_job_at ? fmtRelative(u.last_job_at) : "—"}
          tone="muted"
          testId={`user-lastjob-${u.id}`}
        />
      </div>
      <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-muted-foreground">
        seen {fmtRelative(u.last_seen_at)}
      </div>

      {/* Row 3: Actions */}
      <div className="flex flex-wrap gap-1.5 pt-1">
        <button
          onClick={onGrant}
          data-testid={`user-grant-${u.id}`}
          className="px-2.5 py-1 text-xs border border-border hover:bg-secondary/40 transition-colors"
        >
          Grant Pro
        </button>
        {u.plan !== "free" && (
          <button
            onClick={onRevoke}
            className="px-2.5 py-1 text-xs border border-border text-muted-foreground hover:text-red-400 transition-colors"
          >
            Revoke
          </button>
        )}
        <button
          onClick={onBlock}
          data-testid={`user-block-${u.id}`}
          className={`px-2.5 py-1 text-xs border transition-colors ${
            u.is_blocked
              ? "border-emerald-500/40 text-emerald-400"
              : "border-border hover:text-red-400"
          }`}
        >
          {u.is_blocked ? "Unblock" : "Block"}
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value, tone, testId }) {
  const toneClass =
    tone === "ok" ? "text-emerald-400" : tone === "muted" ? "text-muted-foreground" : "";
  return (
    <div>
      <div className="text-[9px] font-mono uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </div>
      <div className={`tabular-nums ${toneClass}`} data-testid={testId}>
        {value}
      </div>
    </div>
  );
}

function SortHeader({ col, sortBy, sortDir, onClick, children }) {
  const active = sortBy === col;
  const arrow = active ? (sortDir === "asc" ? " ↑" : " ↓") : "";
  return (
    <button
      onClick={() => onClick(col)}
      className={`text-left text-[10px] font-mono uppercase tracking-[0.18em] transition-colors ${
        active ? "text-foreground" : "text-muted-foreground hover:text-foreground/70"
      }`}
      data-testid={`users-sort-${col}`}
    >
      {children}{arrow}
    </button>
  );
}

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <label className="flex items-center gap-1.5">
      <span className="text-muted-foreground">{label}:</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="bg-secondary/30 border border-border px-2 py-1 font-mono text-xs uppercase tracking-[0.12em] focus:outline-none focus:border-foreground/40"
      >
        {options.map((o) => (
          <option key={o.v} value={o.v}>{o.l}</option>
        ))}
      </select>
    </label>
  );
}

function GrantDialog({ target, onClose, onDone }) {
  const [plan, setPlan] = useState("pro");
  const [pick, setPick] = useState(DURATIONS[1]);
  const [busy, setBusy] = useState(false);

  if (!target) return null;

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/users/${target.id}/grant`, {
        plan,
        forever: !!pick.forever,
        duration_days: pick.days,
      });
      toast.success(`${plan} granted to @${target.username || target.first_name}`);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-background border border-border" data-testid="grant-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Grant subscription</DialogTitle>
          <DialogDescription className="text-xs font-mono text-muted-foreground">
            Manually upgrade this user's plan with a fixed expiry.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="text-sm">
            <span className="text-muted-foreground">User: </span>
            <span className="font-mono">
              @{target.username || target.first_name} · {target.telegram_user_id}
            </span>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground mb-2">
              Plan
            </div>
            <div className="flex gap-1">
              {["pro", "pro_plus"].map((p) => (
                <button
                  key={p}
                  onClick={() => setPlan(p)}
                  className={`px-3 py-1.5 text-xs font-mono uppercase border ${
                    plan === p
                      ? "bg-foreground text-background border-foreground"
                      : "border-border text-muted-foreground"
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground mb-2">
              Duration
            </div>
            <div className="flex gap-1 flex-wrap">
              {DURATIONS.map((d) => (
                <button
                  key={d.label}
                  onClick={() => setPick(d)}
                  className={`px-3 py-1.5 text-xs font-mono uppercase border ${
                    pick.label === d.label
                      ? "bg-foreground text-background border-foreground"
                      : "border-border text-muted-foreground"
                  }`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-border"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy}
            data-testid="grant-confirm-btn"
            className="px-4 py-2 text-sm bg-foreground text-background disabled:opacity-50"
          >
            {busy ? "Granting…" : "Grant"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
